/**
 * React Query hooks for Org Admin Tools Management
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getOrgAdminTools,
  getOrgAdminToolStats,
  getOrgAdminTool,
  updateToolApproval,
  createPrivateTool,
  updatePrivateTool,
  deletePrivateTool,
} from "@services/orgAdminTools";
import type {
  TenantToolFilters,
  ToolApprovalUpdate,
  TenantPrivateToolCreate,
  TenantPrivateToolUpdate,
} from "@/types/adminTools";

// Query keys
export const orgAdminToolsKeys = {
  all: ["org-admin-tools"] as const,
  lists: () => [...orgAdminToolsKeys.all, "list"] as const,
  list: (filters?: TenantToolFilters) =>
    [...orgAdminToolsKeys.lists(), filters] as const,
  stats: () => [...orgAdminToolsKeys.all, "stats"] as const,
  details: () => [...orgAdminToolsKeys.all, "detail"] as const,
  detail: (id: string) => [...orgAdminToolsKeys.details(), id] as const,
};

/**
 * Hook to fetch org admin tools with filters
 */
export function useOrgAdminTools(filters?: TenantToolFilters) {
  return useQuery({
    queryKey: orgAdminToolsKeys.list(filters),
    queryFn: () => getOrgAdminTools(filters),
  });
}

/**
 * Hook to fetch org admin tool stats
 */
export function useOrgAdminToolStats() {
  return useQuery({
    queryKey: orgAdminToolsKeys.stats(),
    queryFn: getOrgAdminToolStats,
  });
}

/**
 * Hook to fetch a single tool
 */
export function useOrgAdminTool(toolId: string) {
  return useQuery({
    queryKey: orgAdminToolsKeys.detail(toolId),
    queryFn: () => getOrgAdminTool(toolId),
    enabled: !!toolId,
  });
}

/**
 * Hook to update tool approval status (hide/show)
 */
export function useUpdateToolApproval() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      toolId,
      data,
    }: {
      toolId: string;
      data: ToolApprovalUpdate;
    }) => updateToolApproval(toolId, data),
    onSuccess: () => {
      // Invalidate all tool queries to refresh the list
      queryClient.invalidateQueries({ queryKey: orgAdminToolsKeys.all });
    },
  });
}

/**
 * Hook to create a private tool
 */
export function useCreatePrivateTool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TenantPrivateToolCreate) => createPrivateTool(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orgAdminToolsKeys.all });
    },
  });
}

/**
 * Hook to update a private tool
 */
export function useUpdatePrivateTool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      toolId,
      data,
    }: {
      toolId: string;
      data: TenantPrivateToolUpdate;
    }) => updatePrivateTool(toolId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: orgAdminToolsKeys.all });
      queryClient.invalidateQueries({
        queryKey: orgAdminToolsKeys.detail(variables.toolId),
      });
    },
  });
}

/**
 * Hook to delete a private tool
 */
export function useDeletePrivateTool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (toolId: string) => deletePrivateTool(toolId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orgAdminToolsKeys.all });
    },
  });
}
